// Countermeasure program — every priced deficiency site in one ranked list with
// program totals, editable unit costs (placeholders shipped with the CMF
// library; the customer types bid prices) and a CSV export for HSIP work.
// Totals de-duplicate overlapping sites (see cmfBenefit.buildProgram).
import React, { useMemo, useState } from 'react';
import CmfCard, { Stars } from './CmfCard';
import { buildProgram, programCsv } from './cmfBenefit';
import { fmtUsd } from './insightsText';

const RANKS = [
  { key: 'benefit', label: 'Annual benefit' },
  { key: 'bc', label: 'B/C ratio' },
  { key: 'prevented', label: 'Crashes prevented' },
];
const LIMITS = [10, 25, 50, 0];
const fmtMp = (v) => (v === null || v === undefined ? '—' : v.toFixed(3));

export default function ProgramPanel({ recommendations, exposure, ratePct, costSource, policyLabel,
  corridorLabel, onUnitCost, onFocus }) {
  const [rank, setRank] = useState('benefit');
  const [limit, setLimit] = useState(25);
  const [openKey, setOpenKey] = useState(null);
  const program = useMemo(() => buildProgram(recommendations, rank), [recommendations, rank]);
  const { rows, totals } = program;

  if (!rows.length) {
    return (
      <div className="safety-muted">
        No priced countermeasures in the current selection — no deficiency site has crashes
        matching a CMF's scope under the selected quality policy.
      </div>
    );
  }
  const shown = limit ? rows.slice(0, limit) : rows;

  const exportCsv = () => {
    const csv = programCsv(program, {
      note: `${corridorLabel} · exposure ${exposure} yr · discount ${ratePct}% · ${policyLabel} · costs: ${costSource}`,
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `countermeasure_program_${(corridorLabel || 'corridor').replace(/[^\w.-]+/g, '_')}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <div className="safety-insights">
      <div className="safety-program-stats">
        <div className="safety-stat"><div className="k">Sites with a recommendation</div>
          <div className="v">{totals.sites}{totals.counted < totals.sites && <small> ({totals.counted} after overlap)</small>}</div></div>
        <div className="safety-stat"><div className="k">Expected crashes prevented</div>
          <div className="v">{totals.preventedPerYr} <small>/ yr</small></div></div>
        <div className="safety-stat"><div className="k">Annual benefit</div>
          <div className="v safety-benefit">{fmtUsd(totals.benefitPerYr)}</div></div>
        <div className="safety-stat"><div className="k">Program B/C <small>(PV {fmtUsd(totals.pvBenefit)} vs cost {fmtUsd(totals.unitCost)})</small></div>
          <div className={`v ${totals.bcRatio >= 1 ? 'safety-bc-good' : 'safety-bc-weak'}`}>{totals.bcRatio ?? '—'}</div></div>
      </div>

      <div className="safety-insights-nav">
        <label className="safety-filter">
          <span>Rank by</span>
          <select value={rank} onChange={(e) => setRank(e.target.value)}>
            {RANKS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
        <label className="safety-filter">
          <span>Show</span>
          <select value={limit} onChange={(e) => setLimit(+e.target.value)}>
            {LIMITS.map((n) => <option key={n} value={n}>{n ? `Top ${n}` : 'All'}</option>)}
          </select>
        </label>
        <button type="button" className="safety-btn" onClick={exportCsv}>Export CSV</button>
        <span className="safety-muted">exposure {exposure} yr · discount {ratePct}% · {policyLabel}</span>
      </div>
      <p className="safety-muted safety-program-hint">
        Click a row for the CMF details (study, star rating, matching crashes, arithmetic) — the map
        flies to the site. Dimmed rows share a location with a higher-ranked row: they stay listed
        so you can compare countermeasures, but only the larger benefit counts in the totals.
      </p>

      <div className="safety-table-scroll">
        <table className="safety-table safety-table-clickable">
          <thead>
            <tr>
              <th /><th title="Rank under the selected ordering">#</th>
              <th title="Deficiency site: analysis, corridor, milepost range, direction">Site</th>
              <th title="Best-rated countermeasure for this analysis under the CMF quality policy (FHWA CMF Clearinghouse)">Countermeasure</th>
              <th title="Crash Modification Factor: expected crashes after ÷ before (0.42 = 58% fewer). Stars = Clearinghouse quality rating.">CMF</th>
              <th title="Crashes at the site (±0.1 mi) inside the CMF's own scope — e.g. run-off-road injury crashes for a guardrail, wet-surface crashes for a friction treatment">Matching</th>
              <th title="Matching crashes ÷ exposure years × (1 − CMF)">Prevented / yr</th>
              <th title="Each prevented crash valued at its KABCO comprehensive unit cost, per year">Benefit / yr</th>
              <th title="Countermeasure service life (FHWA service-life synthesis)">Life</th>
              <th title="Present value of the annual benefit over the service life at the discount rate: benefit/yr × (1 − (1+r)^−life) ÷ r">PV benefit</th>
              <th title="Editable — placeholders from the CMF library until you enter bid prices">Unit cost (edit)</th>
              <th title="Benefit-cost ratio = PV benefit ÷ unit cost. 13.8 means every $1 spent is expected to return $13.8 of avoided crash cost over the service life; ≥ 1 pays for itself.">B/C</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const open = openKey === r.key;
              return (
                <React.Fragment key={r.key}>
                  <tr className={`${open ? 'safety-row-open' : ''} ${r.countsInTotal ? '' : 'safety-row-dim'}`}
                    onClick={() => {
                      // row click = open the CMF details (same card as the sites
                      // table) AND fly the map to the site
                      setOpenKey(open ? null : r.key);
                      if (onFocus) onFocus({
                        lat: null, key: `prog-${r.key}-${Date.now()}`, mpStart: r.mpStart, mpEnd: r.mpEnd,
                        analysis: r.analysis, direction: r.direction, corridor: r.corridor,
                      });
                    }}
                    title={open ? 'Hide CMF details' : 'Show CMF details and fly the map to this site'}>
                    <td className="safety-expander">{open ? '▾' : '▸'}</td>
                    <td>{r.rank}</td>
                    <td>
                      <span className={`safety-an safety-an-${r.analysis}`}>{r.analysis}</span>
                      {' '}{r.road ? `${r.road} · ` : ''}MP {fmtMp(r.mpStart)}–{fmtMp(r.mpEnd)} {r.direction === 'P' ? 'EB' : r.direction === 'N' ? 'WB' : ''}
                      {!r.countsInTotal && (
                        <span className="safety-overlap-tag"
                          title={`Same location as row #${r.overlapsRank}; the totals count that row's larger benefit only (the HSM warns against adding or multiplying overlapping CMFs)`}>
                          overlaps #{r.overlapsRank} · not in totals
                        </span>
                      )}
                    </td>
                    <td className="safety-cell-wrap">{r.entry.name}</td>
                    <td>{r.entry.cmf} <Stars entry={r.entry} /></td>
                    <td>{r.matchCount}</td>
                    <td title={`${r.matchCount} matching ÷ ${exposure} yr × (1 − ${r.entry.cmf}) = ${r.preventedPerYr}`}>{r.preventedPerYr}</td>
                    <td title="prevented crashes × KABCO unit cost, per year"><span className="safety-benefit">{fmtUsd(r.benefitPerYr)}</span></td>
                    <td>{r.life} yr</td>
                    <td title={`${fmtUsd(r.benefitPerYr)}/yr × PV factor for ${r.life} yr at ${ratePct}% = ${fmtUsd(r.pvBenefit)}`}>{fmtUsd(r.pvBenefit)}</td>
                    <td className="safety-cell-input" onClick={(e) => e.stopPropagation()}>
                      <input type="number" min={0} step={1000} value={r.unitCost}
                        title={r.entry.unitCostNote || 'placeholder'}
                        onChange={(e) => onUnitCost && onUnitCost(r.key, r.entry.crfid, +e.target.value)} />
                    </td>
                    <td className={r.bcRatio >= 1 ? 'safety-bc-good' : 'safety-bc-weak'}
                      title={`PV benefit ${fmtUsd(r.pvBenefit)} ÷ unit cost ${fmtUsd(r.unitCost)} = ${r.bcRatio ?? '—'}`}>{r.bcRatio ?? '—'}</td>
                  </tr>
                  {open && (
                    <tr className="safety-site-crashes">
                      <td colSpan={12}><CmfCard rec={r} exposure={exposure} ratePct={ratePct} /></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="safety-muted safety-insight-source">
        Prevented / yr = matching crashes ÷ {exposure} exposure years × (1 − CMF); benefit values each
        prevented crash at {costSource}. PV = annual benefit × present-value factor for the service life
        at {ratePct}%. Unit costs are editable placeholders from the CMF library, not bids. Where two
        analyses flag the same stretch, both rows are listed and the totals take the larger — the
        Highway Safety Manual warns that multiplying CMFs overstates combined benefit. Sun-glare (GLR)
        sites have no published CMF and are not priced.
      </p>
    </div>
  );
}
